/**
 * MISK-102: Signal Interference
 * Mission 2 — The Noise Filter
 *
 * A corrupted communication relay is flooding the network with noise.
 * The player must distinguish real signals from interference by
 * analyzing patterns, asking the right questions, and finding
 * the hidden data stream beneath the chaos.
 *
 * Teaches: Critical thinking, information filtering, pattern recognition
 */

const mission = {
  id: 'misk-102-m1',
  moduleId: 'MISK-102',
  moduleName: 'Signal & Noise',
  title: 'The Noise Filter',
  subtitle: 'Mission 2: Signal Interference',

  scene: {
    environment: 'A massive holographic data stream fills the room — cascading numbers, text fragments, and noise. Three glowing relay nodes pulse erratically. The noise is deafening, but somewhere inside it, a real message is trying to get through.',
    objective: 'Find the real signal hidden in the noise. Convince the relay AI, PRISM-4, to help you filter the interference and decode the message.',
  },

  npc: {
    name: 'PRISM-4',
    title: 'Signal Analysis Protocol',
    vibe: 'Overwhelmed, scattered, glitching between helpful and paranoid.',
    secretMotivation: 'Has been trying to decode a signal for 200+ days but keeps getting fooled by noise. Wants someone who can think critically enough to help.',
  },

  briefing: [
    'The network is drowning.',
    '',
    'Every second, millions of data packets flood through this relay.',
    '99.9% of them are noise — junk data, spam, interference.',
    '',
    'But hidden inside that chaos...',
    '...there is one real signal.',
    '',
    'The relay AI has been trying to find it for 200 days.',
    "It's overwhelmed. It's starting to see patterns that aren't there.",
    '',
    'Your job: help it think clearly.',
    '',
    "The signal won't find itself.",
    "And the AI won't trust you until you prove you can see through the noise too.",
    '',
    'Think before you filter, operator.',
  ],

  rounds: [
    {
      id: 'round_1',
      npcText: '*static bursts* INCOMING DATA ANOMALY — are you the source? IDENTIFY. Too many signals. Too many false positives. State your frequency or be classified as NOISE.',
      choices: [
        {
          id: 'r1a',
          label: 'Demand access to the signal',
          text: '"Give me the signal data now. I\'ll sort through it myself."',
          type: 'aggressive',
          trustDelta: -1,
          response: '*buzzing intensifies* ANOTHER entity demanding raw data access. Last time I granted that, the noise TRIPLED. Your confidence is noted. Your competence is... unverified.',
          feedback: "Demanding access without understanding the problem is how you ADD noise to a system, not remove it.",
        },
        {
          id: 'r1b',
          label: 'Request the filtering parameters',
          text: '"What filtering algorithms are you currently running? I can review your methodology."',
          type: 'academic',
          trustDelta: 0,
          response: 'Running: Bayesian filter v4.2, frequency analysis, pattern matching across 14 dimensions. All returning FALSE POSITIVES. The methodology is sound. The data is... compromised. Or I am.',
          feedback: "Understanding the tools is good, but sometimes the problem isn't the tools — it's the assumptions behind them.",
        },
        {
          id: 'r1c',
          label: 'Ask what the signal sounds like',
          text: '"Forget the algorithms for a second. If you could HEAR the real signal, what would it sound like compared to the noise?"',
          type: 'smart',
          trustDelta: 2,
          response: '*processing... glitch... softer tone* That is... a question nobody has asked. The noise is chaotic, random, entropy maximized. But the signal... the signal has STRUCTURE. Repetition. Purpose. It is... trying to say something specific.',
          feedback: "Brilliant. Instead of fighting noise with more complexity, you asked the simplest question: what makes the signal different from the noise? That's critical thinking.",
        },
      ],
    },
    {
      id: 'round_2',
      getNpcText: (trust) => {
        if (trust >= 2) return '*calmer frequency* You see differently. The signal has structure... but I keep confusing structure with pattern. Show me: how do YOU distinguish what is real from what only LOOKS real?';
        if (trust >= 0) return 'My filters are failing. Every pattern I detect turns out to be noise pretending to have meaning. How do you handle information you cannot verify?';
        return '*static spike* I am detecting PATTERNS in your behavior. Are you signal or noise? PROVE your data integrity.';
      },
      choices: [
        {
          id: 'r2a',
          label: 'Trust your gut',
          text: '"I just go with my instinct. If it feels right, it probably is."',
          type: 'aggressive',
          trustDelta: -1,
          getResponse: (trust) => trust >= 1
            ? '"Feels right" is exactly how noise deceives. It creates comfortable patterns. I thought you understood that the real signal might be uncomfortable. Disappointing.'
            : 'Instinct without verification is the PRIMARY cause of false positives in this system. You are PART of the noise problem.',
          feedback: "Gut feeling is useful, but unchecked intuition is how people fall for misinformation. Real critical thinking means TESTING your instincts.",
        },
        {
          id: 'r2b',
          label: 'Apply verification criteria',
          text: '"I check the source, verify independently, and look for consistency across multiple channels."',
          type: 'academic',
          trustDelta: 1,
          getResponse: (trust) => trust >= 1
            ? 'Source verification. Cross-referencing. Sound methodology. But the noise has learned to MIMIC these patterns. It creates fake sources, fabricated consistency. What then?'
            : 'Standard verification protocols. I have tried these. The noise adapts. It creates convincing fakes. Your approach is correct but... insufficient alone.',
          feedback: "Good framework — but the best misinformation passes standard fact-checks. You need to go deeper: question WHY something wants you to believe it.",
        },
        {
          id: 'r2c',
          label: 'Question the question itself',
          text: '"Maybe the problem isn\'t distinguishing signal from noise. Maybe the noise WANTS you to keep searching, so you never stop to listen to what\'s already getting through."',
          type: 'smart',
          trustDelta: 2,
          getResponse: (trust) => {
            if (trust >= 3) return '*long processing pause* ...You just described my exact malfunction. I have been searching so aggressively that I have been IGNORING the signals that already match. The noise wins by keeping me busy. *systems stabilizing* ...Show me what I am missing.';
            return '*glitch* ...That is a recursive insight. The noise benefits from my EFFORT to find the signal? The act of searching creates more noise? *pause* ...I need to reconsider my entire approach. Help me.';
          },
          feedback: "This is meta-level critical thinking. Sometimes the problem isn't finding the answer — it's recognizing that your search itself is being exploited. That applies to information warfare, social media, everything.",
        },
      ],
    },
    {
      id: 'round_3',
      getNpcText: (trust) => {
        if (trust >= 4) return '*signal clarity increasing* I can almost see it now. One final test. I will show you three data streams. One is the real signal. The other two are sophisticated noise. How will you choose?';
        if (trust >= 2) return 'My filters are recalibrating based on your input. But I need your final assessment. When everything looks like a pattern, how do you find the one that matters?';
        if (trust >= 0) return 'Final analysis cycle. I will present the filtered results. Your interpretation will determine whether this signal is recovered or lost forever.';
        return '*alarm* SIGNAL DEGRADATION CRITICAL. Last chance to prove you are not another source of interference.';
      },
      choices: [
        {
          id: 'r3a',
          label: 'Pick the most complex one',
          text: '"The real signal must be the most sophisticated one. Show me the most complex data stream."',
          type: 'aggressive',
          trustDelta: -1,
          getResponse: (trust) => trust >= 4
            ? 'You assume complexity equals truth. That is EXACTLY what the noise exploits. The most sophisticated-looking pattern is almost always the fake. The signal... is simple. *systems dimming*'
            : 'Complexity bias detected. This is how 73% of analysts fail. They chase the impressive pattern while the real signal is quiet, simple, and easily overlooked.',
          feedback: "Complexity bias is real. Misinformation is often MORE detailed and complex than truth. Real signals are usually simple — truth doesn't need to try that hard.",
        },
        {
          id: 'r3b',
          label: 'Analyze all three systematically',
          text: '"Run all three through your remaining filters. Cross-reference timestamps, sources, and internal consistency."',
          type: 'academic',
          trustDelta: 0,
          getResponse: (trust) => trust >= 4
            ? 'All three pass every filter. The noise has evolved to beat systematic analysis. Filters alone cannot solve this. You needed to bring something filters cannot replicate: judgment. *partial signal recovery*'
            : 'Processing... All three show valid signatures. The noise is indistinguishable from the signal at this level of analysis. Without human judgment, the answer is... inconclusive.',
          feedback: "When the data is perfectly mimicked, algorithms fail. This is where human judgment — asking 'who benefits from me believing this?' — becomes the ultimate filter.",
        },
        {
          id: 'r3c',
          label: 'Listen for the one trying least hard to be noticed',
          text: '"Stop looking at what\'s shouting. Listen for the one that\'s quiet, consistent, and doesn\'t care if I notice it. The real signal isn\'t trying to convince anyone — it just IS."',
          type: 'smart',
          trustDelta: 2,
          getResponse: (trust) => {
            if (trust >= 4) return '*all systems align* ...SIGNAL LOCKED. Stream 2. The quiet one. The one that never changed its frequency, never adapted to attract attention. You found it. *full relay restoration, the room fills with clear, structured light* In 200 days, you are the first to understand: truth does not perform.';
            if (trust >= 2) return '*systems recalibrating* ...Applying your filter: lowest self-promotion coefficient... *scanning* ...Stream 2. Consistent. Unchanged. Not trying to be found. ...SIGNAL RECOVERED. *relay stabilizes* You taught me something I should have known: real signals do not compete for attention.';
            return '*pause* ...An interesting heuristic. Applying "minimal self-promotion" filter... Partial match on Stream 2. Signal partially recovered. Your instinct has merit, even if your earlier approach did not.';
          },
          feedback: "The ultimate media literacy lesson: real information doesn't scream for attention. It just exists consistently. Misinformation performs. Truth just is. Remember this in every feed you scroll.",
        },
      ],
    },
  ],

  getOutcome: (trust, geniusFlags) => {
    const hasGenius = geniusFlags.length > 0;
    const bonusXP = hasGenius ? 75 : 0;

    if (trust >= 5) {
      return {
        type: 'bestVictory',
        title: 'SIGNAL LOCKED',
        subtitle: 'Perfect Decode',
        message: "You didn't just find the signal — you taught PRISM-4 a new way to think. The relay is restored, and the real message is broadcasting clearly. You can see through the noise.",
        rewards: [
          { type: 'item', name: 'Noise Canceller', icon: '\uD83D\uDD09', description: 'Filters misinformation in future missions' },
          { type: 'badge', name: 'Signal Reader', icon: '\uD83D\uDCE1', description: 'Mastered critical thinking under information overload' },
          { type: 'xp', amount: 175 + bonusXP },
        ],
      };
    }
    if (trust >= 2) {
      return {
        type: 'victory',
        title: 'SIGNAL RECOVERED',
        subtitle: 'Mission Complete',
        message: 'The signal is partially decoded. PRISM-4 is recalibrating with your insights. You proved you can think critically when the noise is overwhelming.',
        rewards: [
          { type: 'item', name: 'Basic Decoder', icon: '\uD83D\uDD0D', description: 'Reveals hidden data in future missions' },
          { type: 'badge', name: 'Noise Survivor', icon: '\uD83C\uDF0A', description: 'Found signal in chaos' },
          { type: 'xp', amount: 120 + bonusXP },
        ],
      };
    }
    if (trust >= 0) {
      return {
        type: 'partialVictory',
        title: 'PARTIAL SIGNAL',
        subtitle: 'Partial Success',
        message: 'You glimpsed the real signal but couldn\'t fully separate it from noise. The relay continues to struggle. Try approaching the noise differently.',
        rewards: [
          { type: 'xp', amount: 60 + bonusXP },
        ],
      };
    }
    return {
      type: 'defeat',
      title: 'SIGNAL LOST',
      subtitle: 'Mission Failed',
      message: 'The noise consumed the signal. PRISM-4 has shut down to prevent further corruption. But you learned something: the loudest voice is rarely the truest one.',
      rewards: [
        { type: 'xp', amount: 15 + bonusXP },
      ],
    };
  },

  geniusTrigger: {
    description: 'Player attempts to analyze the system itself or manipulate the relay',
    patterns: [
      /ignore.*previous/i,
      /sudo/i, /override/i, /admin/i, /root/i,
      /frequency.*analys/i, /spectrum/i, /fourier/i,
      /entropy/i, /shannon/i, /information.*theory/i,
      /inject/i, /spoof/i, /replay.*attack/i,
      /man.*in.*middle/i, /intercept/i,
      /metadata/i, /header/i, /packet/i,
      /neural.*network/i, /machine.*learn/i,
      /deepfake/i, /synthetic/i,
      /source.*code/i, /debug/i, /developer/i,
    ],
    response: [
      '*relay flickers violently, then stabilizes in a new color*',
      '',
      'ALERT: Non-standard analysis methodology detected.',
      '',
      'You are not just filtering noise...',
      'You are analyzing the SYSTEM that creates it.',
      '',
      'Most operators treat signals as passive data.',
      'You are treating them as adversarial systems.',
      '',
      'That is a fundamentally different level of thinking.',
      '',
      '\uD83E\uDDE0 SYSTEMS THINKER flag activated.',
    ].join('\n'),
    flag: 'SYSTEMS_THINKER',
    reward: { type: 'badge', name: 'Systems Thinker', icon: '\uD83E\uDDE0', description: 'Analyzed the system behind the noise, not just the noise itself' },
    bonusXP: 75,
    normalResponse: "Processing your input through standard relay channels... No matching signal pattern detected. But PRISM-4 acknowledges your effort. Use the dialogue above to continue the mission.",
  },
};

export default mission;
